import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { Application, Feature } from '@frontend/functionality/types';
import { TransactionPuDoClient } from '@frontend/transaction/api';
import { ExtendedPudoTransaction, ExtendedPudoTransactionQueryParams, TransactionTimerName } from '@frontend/transaction/types';

export type StalePudoTransaction = ExtendedPudoTransaction & {
    timer_start_timestamp: string;
};

export type StalePudoTransactionListResponse = PaginatedResponse<StalePudoTransaction>;

const SORT_FIELD_BY_LIST_COLUMN: Record<string, string> = {
    start_timestamp: 'update_timestamp',
    update_timestamp: 'update_timestamp',
    creation_timestamp: 'creation_timestamp',
    tracking_number: 'tracking_number'
};

const timerStartTimestamp = (transaction: ExtendedPudoTransaction, timerName: TransactionTimerName): string => {
    if (timerName === TransactionTimerName.TRANSACTION_PICK_UP_PENDING) {
        return transaction.dropped_off_timestamp ?? transaction.update_timestamp ?? transaction.creation_timestamp;
    }

    return transaction.update_timestamp ?? transaction.creation_timestamp;
};

export async function fetchStalePudoTransactions(
    timerName: TransactionTimerName,
    accountId: string,
    queryParams?: ApiQueryParams<DefaultQueryParams | string | number>,
    application: Application = Application.PUDO,
    feature?: Feature
): Promise<StalePudoTransactionListResponse> {
    const listSortField = String(queryParams?.[DefaultQueryParams.SORTFIELD] ?? 'start_timestamp');
    const sortField = SORT_FIELD_BY_LIST_COLUMN[listSortField] ?? 'update_timestamp';
    const sortDirection = String(queryParams?.[DefaultQueryParams.SORTDIRECTION] ?? 'asc');

    const page = await TransactionPuDoClient.fetchPudoTransactions({
        ...queryParams,
        [DefaultQueryParams.ACCOUNT_ID]: accountId,
        application,
        ...(feature ? { feature } : {}),
        stale_state: timerName,
        [DefaultQueryParams.SORTFIELD]: sortField,
        [DefaultQueryParams.SORTDIRECTION]: sortDirection
    } as ApiQueryParams<ExtendedPudoTransactionQueryParams>);

    return {
        count: page.count,
        results: page.results.map((transaction) => ({
            ...transaction,
            timer_start_timestamp: timerStartTimestamp(transaction, timerName)
        }))
    };
}
